import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ChevronDown, Stethoscope, ArrowUpRight, Info } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { api } from '../api';

function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }

function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <div className="relative">
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="appearance-none w-full bg-white border border-[#E5DCC4] rounded-lg h-11 px-3.5 pr-9 text-[13px] text-[#1A2622] outline-none cursor-pointer focus:border-[#4A7C5E]">
        <option value="">{placeholder}</option>
        {options.map((o, i) => <option key={i} value={typeof o === 'string' ? o : o.value}>{typeof o === 'string' ? o : o.label}</option>)}
      </select>
      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#7A857F]"/>
    </div>
  );
}

function SpecBadge({ type }) {
  const styles = {
    PMR: { bg: '#E5F0EA', fg: '#2D5F4F', label: 'PM&R' },
    IM: { bg: '#FBE6CC', fg: '#A06628', label: 'IM' },
    APP: { bg: '#E0EAF8', fg: '#34538F', label: 'NP/PA' },
  };
  const s = styles[type];
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: s.bg, color: s.fg }}>{s.label}</span>;
}

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

export default function PhysiciansPage() {
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [state, setState] = useState(params.get('state') || '');
  const [specialty, setSpecialty] = useState('');
  const [bucket, setBucket] = useState('');
  const [minServices, setMinServices] = useState('');
  const [sort, setSort] = useState('services');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const LIMIT = 40;

  useEffect(() => { setPage(0); }, [q, state, specialty, bucket, minServices, sort]);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const p = {
        q: q || undefined,
        state: state || undefined,
        specialty: specialty || undefined,
        min_services: minServices || undefined,
        sort,
        limit: LIMIT,
        offset: page * LIMIT,
      };
      if (bucket === 'pm_r') p.pm_r = true;
      else if (bucket === 'internal_med') p.internal_med = true;
      else if (bucket === 'app') p.app = true;
      api.physicians(p).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, state, specialty, bucket, minServices, sort, page]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / LIMIT));
  const lastUpdated = useMemo(() => new Date().toLocaleDateString(), []);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-12">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Physicians' }]} />

        <header className="mt-6 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
              <Stethoscope size={12} className="text-[#4A7C5E]"/>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">Physician directory · public Part B SNF claims</span>
            </div>
            <h1 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-[#1A2622] tracking-tight">Physicians billing SNF Medicare</h1>
            <p className="mt-2 text-[13.5px] text-[#3A4A44] max-w-2xl">Every physician with a public Medicare Part B claim for SNF E&M codes (99304-99316). Filter by specialty, geography, and SNF claims volume.</p>
          </div>
          <Link to="/physician-intelligence" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-[#1A2622] text-[#FBF6EC] text-[13px] hover:bg-[#2D5F4F]" data-testid="phys-intel-link">
            Intelligence dashboard <ArrowUpRight size={14}/>
          </Link>
        </header>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-4 relative">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7A857F]"/>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or group practice…" className="w-full bg-white border border-[#E5DCC4] rounded-lg h-11 pl-10 pr-3 text-[13px] text-[#1A2622] placeholder:text-[#A8B0AA] outline-none focus:border-[#4A7C5E]" data-testid="phys-search"/>
          </div>
          <div className="md:col-span-2"><Select testId="phys-filter-state" value={state} onChange={setState} options={US_STATES} placeholder="All states"/></div>
          <div className="md:col-span-2"><Select testId="phys-filter-bucket" value={bucket} onChange={setBucket} options={[{value:'pm_r',label:'PM&R only'},{value:'internal_med',label:'IM only'},{value:'app',label:'NP / PA only'}]} placeholder="Any specialty type"/></div>
          <div className="md:col-span-2">
            <input value={specialty} onChange={(e) => setSpecialty(e.target.value)} placeholder="Specialty contains…" className="w-full bg-white border border-[#E5DCC4] rounded-lg h-11 px-3.5 text-[13px] outline-none focus:border-[#4A7C5E]" data-testid="phys-filter-specialty"/>
          </div>
          <div className="md:col-span-1">
            <input value={minServices} onChange={(e) => setMinServices(e.target.value.replace(/\D/g, ''))} placeholder="Min srv" className="w-full bg-white border border-[#E5DCC4] rounded-lg h-11 px-3.5 text-[13px] outline-none focus:border-[#4A7C5E]" data-testid="phys-filter-min"/>
          </div>
          <div className="md:col-span-1"><Select value={sort} onChange={setSort} options={[{value:'services',label:'Srvs'},{value:'beneficiaries',label:'Benes'},{value:'name',label:'Name'}]} placeholder="Sort"/></div>
        </div>

        <div className="mt-3 text-[11.5px] text-[#7A857F]">{loading ? 'Loading…' : `${fmtInt(data.total)} physicians match`}</div>

        <section className="mt-3 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                <tr>
                  <th className="text-left px-4 py-2.5">Physician</th>
                  <th className="text-left px-4 py-2.5">Specialty</th>
                  <th className="text-left px-4 py-2.5">Group / practice</th>
                  <th className="text-right px-4 py-2.5">SNF srv</th>
                  <th className="text-right px-4 py-2.5">Benes</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => (
                  <tr key={p.npi} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`phys-list-row-${p.npi}`}>
                    <td className="px-4 py-2.5">
                      <Link to={`/physicians/${p.npi}`} className="text-[#1A2622] font-medium hover:text-[#2D5F4F]">
                        {p.name}{p.credentials ? `, ${p.credentials}` : ''}
                      </Link>
                      <div className="text-[10.5px] text-[#7A857F]">NPI {p.npi} · {p.city || ''}{p.city && p.state ? ', ' : ''}{p.state || ''}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="text-[#3A4A44] truncate max-w-[240px]" title={p.specialty}>{p.specialty || '—'}</div>
                      <div className="mt-1 flex gap-1">
                        {p.is_pm_r && <SpecBadge type="PMR"/>}
                        {p.is_internal_med && <SpecBadge type="IM"/>}
                        {p.is_app && <SpecBadge type="APP"/>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-[#3A4A44] truncate max-w-[240px]" title={p.primary_group}>{p.primary_group || '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{fmtInt(p.snf_services)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(p.snf_beneficiaries)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="mt-4 flex items-center justify-between text-[12px] text-[#5A6660]">
          <div>Page {page + 1} of {totalPages}</div>
          <div className="flex gap-1">
            <button disabled={page === 0} onClick={() => setPage(page - 1)} className="px-3 py-1.5 rounded-md bg-white border border-[#E5DCC4] disabled:opacity-50">Prev</button>
            <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 rounded-md bg-white border border-[#E5DCC4] disabled:opacity-50">Next</button>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA]">
          <Info size={12} className="mt-0.5 shrink-0"/>
          <span><strong>Source:</strong> {data.source}. <strong>Secondary:</strong> {data.secondary_source}. <strong>Last refresh:</strong> {lastUpdated}. Wording: "likely SNF activity" — these are public-claims estimates, not patient referrals.</span>
        </div>
      </main>
      <Footer />
    </div>
  );
}
